import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/news", label: "影视资讯" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setSearchOpen(false);
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950">
            <i className="ri-film-fill text-lg"></i>
          </span>
          <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
            电影<span className="text-primary-600">天堂</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              location.pathname === "/"
                ? "text-primary-600"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            首页
          </Link>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:text-primary-600"
            >
              电影分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-2">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                    >
                      <i className={`${c.icon} text-base`}></i>
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {navItems.slice(1).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === item.to
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-full max-w-xs items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 lg:flex"
        >
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索电影 / 资讯"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
          />
        </form>

        <button
          type="button"
          aria-label="搜索"
          onClick={() => setSearchOpen(true)}
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-200 lg:hidden"
        >
          <i className="ri-search-line text-lg"></i>
        </button>

        <button
          type="button"
          aria-label="菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-200 md:hidden"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="mt-2 border-t border-background-200 pt-3">
            <p className="mb-2 px-3 text-xs font-medium text-foreground-500">电影分类</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className={`${c.icon} text-base`}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 px-4 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5">
              <i className="ri-search-line text-lg text-foreground-500"></i>
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 资讯"
                className="w-full bg-transparent text-base text-foreground-900 outline-none placeholder:text-foreground-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="rounded-md px-3 py-2 text-sm text-foreground-600"
            >
              取消
            </button>
          </form>
        </div>
      )}
    </header>
  );
}